import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { booleanSchema } from '@zaoblako/shared/modules/serde/schemas/boolean.schema';
import {
  ContragentFindDto,
  type ContragentFindInterface,
} from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { FilialFindDto, type FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import {
  ExternalUserFindDto,
  type ExternalUserFindInterface,
} from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import {
  TransitTaskCarCategoryLimitFindDto,
  type TransitTaskCarCategoryLimitFindInterface,
} from '@zaoblako/shared/modules/client/dtos/transit-task-car-category-limit/transit-task-car-category-limit-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { ClientDtoKeysEnum } from '@zaoblako/shared/modules/client/enums/client-dto-keys.enum';
import { HelpdeskDtoKeyEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk-dto-key.enum';
import type {
  HelpdeskTaskTypeFindInterface,
} from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';

import * as yup from 'yup';

/** Response body for client - logical group of counterparties. */
export interface ClientFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  // Tech columns
  /** Client creation date and time */
  created: Date;
  /** Client last update date and time */
  updated: Date;
  /** Client soft deletion date and time */
  deleted?: Date;
  /** Client creator */
  createdBy?: UserFindInterface;
  /** Client display name */
  name: string;
  /** Max resident cars to pass */
  residentCarLimit?: number;
  /** Total resident cars in the parking */
  residentCarTotal?: number;
  /** If `true`, transit tasks will always be zero-sum */
  freeTransitTask?: boolean;
  /** If `true`, a guest transit task can be created for this client from a camera event */
  isCreateTransitTask?: boolean;
  /**
   * If `true` then parking features are blocked for client: resident car and
   * transit task entrance and CRUD operations are not allowed
   */
  isParkingBlocked?: boolean;

  // Relations
  /** Counterparties belonging to this client */
  contragents?: ContragentFindInterface[];
  /** Filials belonging to this client */
  filials?: FilialFindInterface[];
  /** Contact persons from counterparties */
  externalUsers?: ExternalUserFindInterface[];
  /** HelpDesk tasks belonging to this client */
  helpDeskTasks?: HelpdeskTaskFindInterface[];
  /** Directions belonging to this client */
  helpdeskTaskTypes?: HelpdeskTaskTypeFindInterface[];
  /** Transit task limits for this client by categories */
  transitTaskCarCategoryLimits?: TransitTaskCarCategoryLimitFindInterface[];
}

/** Response body for client - logical group of counterparties. DTO schema */
export const ClientFindDto = SerdeDtoClass.create<ClientFindInterface>(ClientDtoKeysEnum.CLIENT_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Изменен',
    serializer: yup.date(),
  },
  deleted: {
    label: 'Удален',
    optional: true,
    serializer: yup.date(),
  },
  createdBy: {
    label: 'Автор',
    optional: true,
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  name: {
    sortable: true,
    label: 'Название',
    serializer: yup.string(),
  },
  residentCarLimit: {
    optional: true,
    label: 'Лимит постоянных ТС',
    serializer: yup.number(),
  },
  residentCarTotal: {
    optional: true,
    label: 'Общее количество ТС на парковке',
    serializer: yup.number(),
  },
  freeTransitTask: {
    optional: true,
    label: 'Бесплатный гостевой проезд',
    serializer: booleanSchema,
  },
  isCreateTransitTask: {
    optional: true,
    label: 'Создание гостевой заявки из события',
    serializer: booleanSchema,
  },
  isParkingBlocked: {
    optional: true,
    label: 'ТС заблокированы',
    hint: 'Если включено, то будет запрещен въезд для всех постоянных и гостевых ТС клиента, а также какие-либо операции над этими ТС',
    serializer: booleanSchema,
  },
  contragents: {
    label: 'Контрагенты',
    optional: true,
    nested: ContragentFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  filials: {
    label: 'Филиалы',
    optional: true,
    nested: FilialFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  externalUsers: {
    label: 'Внешние пользователи',
    optional: true,
    nested: ExternalUserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  helpDeskTasks: {
    label: 'Заявки HelpDesk',
    optional: true,
    nested: HelpdeskDtoKeyEnum.HELPDESK_TASK_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  helpdeskTaskTypes: {
    label: 'Типы заявок HelpDesk',
    optional: true,
    nested: HelpdeskDtoKeyEnum.HELPDESK_TASK_TYPE_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  transitTaskCarCategoryLimits: {
    label: 'Лимиты гостевых ТС по категориям',
    nested: TransitTaskCarCategoryLimitFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
});
